<template>
  <div class="calendar-wrap">
    <!-- Header -->
    <v-toolbar class="calendar-header">
      <v-btn variant="outlined" @click="goToday" color="primary" class="mx-4">
        {{ $t?.('home.today') ?? 'Dnes' }}
      </v-btn>

      <v-btn icon variant="text" @click="prevMonth">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <v-btn icon variant="text" @click="nextMonth">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>

      <div class="month-title">
        {{ monthTitle }}
      </div>

      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="25"
      />
    </v-toolbar>

    <!-- Calendar -->
    <v-calendar
  v-model="model"
  type="month"
  color="primary"
  :events="calendarItems"
  :weekdays="weekdays"
  :hide-week-number="true"
  :show-adjacent-months="true"
  :event-color="() => 'transparent'"
  :event-more="false"
>
  <template #event="{ event }">
    <div class="simple-item" :style="{ backgroundColor: event.backgroundColor }">
      <div class="d-flex">
        <router-link :to="'/soutez/' + event.eventId" class="event-name text-truncate" :class="{ 'text-decoration-line-through': event.canceled }">
          {{ event.eventName }}
        </router-link>
        <v-spacer />
        <v-icon
          v-if="event.isClubOnly"
          size="16"
          color="black"
          class="ml-1 flex-shrink-0"
        >
          mdi-lock-open
        </v-icon>
      </div>

      <div v-if="event.kind === 'race'" class="race-name text-truncate" :class="{ 'text-decoration-line-through': event.canceled }">
        {{ event.raceName }}
      </div>

      <!-- EVENT: řádek = chips vlevo, ikony vpravo -->
      <div v-if="event.kind !== 'race'" class="meta-event">
        <div class="chips-left">
          <v-chip color="secondary" density="compact">
            <v-icon start>mdi-account</v-icon>
            {{ event.entries ?? 0 }}
          </v-chip>

          <v-chip size="small" class="text-grey-darken-2" density="compact">
            {{ event.clubShortcut }}
          </v-chip>
        </div>

        <div class="icons-right">
          <router-link
            v-if="event.entryIsAllow"
            :to="`/soutez/${event.eventId}/prihlaska/`"
            class="entry-link"
          >
            <v-icon
              :color="event.entry_deadline_is_second ? 'orange-accent-4' : 'green'"
              size="24"
              :title="$t('home.create_entry')"
              class="mr-2"
            >
              mdi-account-plus
            </v-icon>
          </router-link>

          <a v-if="event.bulletin" :href="formatFileLink(event.bulletin)" target="_blank" rel="noopener">
            <v-icon color="primary" size="18" :title="$t('home.bulletin')">mdi-text-box</v-icon>
          </a>
          <a v-if="event.info" :href="formatFileLink(event.info)" target="_blank" rel="noopener">
            <v-icon color="primary" size="18" :title="$t('home.info')">mdi-information-slab-box</v-icon>
          </a>
          <a v-if="event.startlist" :href="formatFileLink(event.startlist)" target="_blank" rel="noopener">
            <v-icon color="primary" size="18" :title="$t('home.startlist')">mdi-timer-play</v-icon>
          </a>
          <router-link
            v-if="isLiveRace(event.firstRaceId)"
            :to="liveResultsPath(event.firstRaceId)"
          >
            <v-icon color="primary" size="18" :title="$t('home.results')">mdi-trophy</v-icon>
          </router-link>
          <a v-else-if="event.results" :href="formatFileLink(event.results)" target="_blank" rel="noopener">
            <v-icon color="primary" size="18" :title="$t('home.results')">mdi-trophy</v-icon>
          </a>
        </div>
      </div>

      <!-- RACE: řádek chipů = space-between (vše rovnoměrně) -->
      <div v-else class="meta-race">
        <v-chip color="secondary" density="compact">
          <v-icon start>mdi-account</v-icon>
          {{ event.entries ?? 0 }}
        </v-chip>

        <v-chip size="small" class="text-grey-darken-2" density="compact">
          {{ event.clubShortcut }}
        </v-chip>

        <v-chip
          v-if="event.raceLevel"
          size="small"
          class="text-deep-orange-darken-2"
          density="compact"
        >
          {{ raceLevelLabel(event.raceLevel) }}
        </v-chip>
      </div>

      <div v-if="event.kind === 'race'" class="icons-row">
        <router-link
          v-if="event.entryIsAllow"
          :to="`/soutez/${event.eventId}/prihlaska/`"
          class="entry-link"
        >
          <v-icon
            :color="event.entry_deadline_is_second ? 'orange-accent-4' : 'green'"
            size="24"
            class="mr-2"
            :title="$t('home.create_entry')"
          >
            mdi-account-plus
          </v-icon>
        </router-link>

        <a v-if="event.bulletin" :href="formatFileLink(event.bulletin)" target="_blank" rel="noopener">
          <v-icon color="primary" size="21" :title="$t('home.bulletin')">mdi-text-box</v-icon>
        </a>
        <a v-if="event.info" :href="formatFileLink(event.info)" target="_blank" rel="noopener">
          <v-icon color="primary" size="21" :title="$t('home.info')">mdi-information-slab-box</v-icon>
        </a>
        <a v-if="event.startlist" :href="formatFileLink(event.startlist)" target="_blank" rel="noopener">
          <v-icon color="primary" size="21" :title="$t('home.startlist')">mdi-timer-play</v-icon>
        </a>
        <router-link
          v-if="isLiveRace(event.raceId)"
          :to="liveResultsPath(event.raceId)"
        >
          <v-icon color="primary" size="21" :title="$t('home.results')">mdi-trophy</v-icon>
        </router-link>
        <a v-else-if="event.results" :href="formatFileLink(event.results)" target="_blank" rel="noopener">
          <v-icon color="primary" size="21" :title="$t('home.results')">mdi-trophy</v-icon>
        </a>
      </div>
    </div>
  </template>
</v-calendar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import apiClient from '/api.js'
import { useAuthStore } from '/auth.js'


const { locale, t } = useI18n()
const theme = useTheme()
const authStore = useAuthStore()

const isLiveRace = (raceId) =>
  authStore.liveResultsActive
  && raceId != null
  && authStore.liveResultsRaceIds.includes(Number(raceId))

const liveResultsPath = (raceId) =>
  `/vysledky/${new Date().getFullYear()}/${raceId}`

/**
 * Model pro v-calendar držím jako "YYYY-MM-DD".
 * To je stabilní (bez posunů časových pásem).
 */
const toKey = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}
const fromKey = (s) => {
  const [y, m, d] = String(s).split('-').map(Number)
  return new Date(y, m - 1, d, 12, 0, 0, 0) // poledne = stabilní přes DST
}

const getYearMonthFromModel = (key) => {
  const y = Number(String(key).slice(0, 4))
  const m = Number(String(key).slice(5, 7)) // "01".."12"
  return { year: y, month: m }
}

const raceLevelKey = (lvl) => String(lvl || '').toUpperCase()

// pastelové pozadí bloků — light / stejný odstín tmavší pro dark
const eventColorsLight = ['#ECEFF1', '#ffebee', '#e8f5e9', '#ede7f6', '#fff8e1', '#e0f7fa', '#f1f8e9']
const eventColorsDark = ['#37474F', '#4A2428', '#1B3D24', '#382B4F', '#4A3F18', '#1A3A42', '#2A3D1F']
const eventColors = computed(() =>
  theme.global.current.value.dark ? eventColorsDark : eventColorsLight
)

const raceLevelAbbr = {
  INTERNATIONAL: 'svět',
  NATIONAL: 'I.',
  REGIONAL: 'II.',
  DISTRICT: 'III.',
  PRACTICE: 'trén',
  OTHER: 'ost',
}

const raceLevelLabel = (lvl) => {
  const key = raceLevelKey(lvl)
  // i18n: home.lvl.NATIONAL apod.
  const i18nKey = `home.lvl.${key}`
  const translated = t(i18nKey)
  // pokud překlad neexistuje, vue-i18n často vrátí klíč
  if (translated && translated !== i18nKey) return translated
  // fallback na lokální zkratky
  return raceLevelAbbr[key] || key || ''
}


const formatFileLink = (link) => {
  const base = String(process.env.VUE_APP_API_BASE_URL || '')
    .replace(/\/+$/, '')
    .replace(/\/api\/?$/, '')
  return `${base}${link}`
}

const loaded = ref({
  year: null,
  period: null,
})

const model = ref(toKey(new Date()))
const loading = ref(false)
const rawEvents = ref([]) // data z API

const weekdays = computed(() => {
  // cs: Po–Ne, en: Ne–So
  return String(locale.value).startsWith('cs')
    ? [1, 2, 3, 4, 5, 6, 0]
    : [0, 1, 2, 3, 4, 5, 6]
})

const monthTitle = computed(() => {
  return new Intl.DateTimeFormat(locale.value, { month: 'long', year: 'numeric' })
    .format(fromKey(model.value))
})

const goToday = () => { model.value = toKey(new Date()) }

const prevMonth = () => {
  const d = fromKey(model.value)
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  model.value = toKey(d)
}

const nextMonth = () => {
  const d = fromKey(model.value)
  d.setDate(1)
  d.setMonth(d.getMonth() + 1)
  model.value = toKey(d)
}

/**
 * Z API vytvoříme pro každý den eventu jednu položku:
 * - pokud má event v ten den race -> title = race_name
 * - jinak title = event_name
 *
 * Výsledkem je seznam "jednodenních" items: start=end=YYYY-MM-DD
 */
const calendarItems = computed(() => {
  const items = []

  for (const ev of rawEvents.value) {
    const startKey = ev.event_date_start
    const endKey = ev.event_date_end || ev.event_date_start

    const eventEntries = ev.entries ?? 0
    const bg = eventColors.value[ev.id % eventColors.value.length]

    // seskup všechny race podle dne (NEvynecháváme žádný index)
    const racesByDay = new Map() // dayKey -> Array<raceInfo>
    if (Array.isArray(ev.races)) {
      for (const r of ev.races) {
        if (!r?.race_date) continue
        if (!racesByDay.has(r.race_date)) racesByDay.set(r.race_date, [])
        racesByDay.get(r.race_date).push({
          raceId: r.id ?? null,
          raceName: r.race_name,
          raceLevel: r.race_level,
          entries: r.entries ?? 0,
          bulletin: r.bulletin ?? null,
          info: r.info ?? null,
          startlist: r.startlist ?? null,
          results: r.results ?? null,
        })
      }
    }

    const d0 = fromKey(startKey)
    const d1 = fromKey(endKey)

    for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
      const dayKey = toKey(d)
      const racesToday = racesByDay.get(dayKey) || []

      // pokud jsou race v tento den -> nezobrazuj event item, jen všechny race
      if (racesToday.length) {
        for (const r of racesToday) {
          items.push({
            start: dayKey,
            end: dayKey,
            allDay: true,
            backgroundColor: bg,

            kind: 'race',
            eventId: ev.id,
            eventName: ev.event_name,

            raceId: r.raceId ?? null,
            raceName: r.raceName,
            raceLevel: r.raceLevel ?? null,
            isClubOnly: ev.visibility === 'CLUB',
            canceled: !!ev.canceled,

            clubShortcut: ev.club_shortcut,

            entries: r.entries ?? 0,

            entryIsAllow: !!ev.entry_is_allow,
            entry_deadline_is_second: !!ev.entry_deadline_is_second,

            // odkazy – pro race preferuj race, fallback na event
            bulletin: r.bulletin || ev.bulletin || null,
            info:     r.info     || ev.info     || null,
            startlist:r.startlist|| null,
            results:  r.results  || null,
          })
        }
        continue
      }

      // v tento den race nejsou -> zobraz event item
      items.push({
        start: dayKey,
        end: dayKey,
        allDay: true,
        backgroundColor: bg,

        kind: 'event',
        eventId: ev.id,
        eventName: ev.event_name,
        firstRaceId: ev.first_race_id ?? null,
        raceName: '',
        raceLevel: null,
        isClubOnly: ev.visibility === 'CLUB',
        clubShortcut: ev.club_shortcut,
        canceled: !!ev.canceled,

        entries: eventEntries,

        entryIsAllow: !!ev.entry_is_allow,
        entry_deadline_is_second: !!ev.entry_deadline_is_second,

        bulletin: ev.bulletin || null,
        info: ev.info || null,
        startlist: ev.startlist || null,
        results: ev.results || null,
      })
    }
  }

  return items
})

const fetchEvents = async (year, month) => {
  loading.value = true
  try {
    const res = await apiClient.get(`/event/?year=${year}&period=${month}`)
    rawEvents.value = Array.isArray(res.data) ? res.data : []
    loaded.value = { year, month }
  } finally {
    loading.value = false
  }
}

const ensureLoadedForModel = (key) => {
  const { year, month } = getYearMonthFromModel(key)
  if (loaded.value.year !== year || loaded.value.month !== month) {
    fetchEvents(year, month)
  }
}

watch(
  model,
  (newKey, oldKey) => {
    if (!oldKey) {
      ensureLoadedForModel(newKey)
      return
    }

    const { year: newY, month: newM } = getYearMonthFromModel(newKey)
    const { year: oldY, month: oldM } = getYearMonthFromModel(oldKey)

    if (newY === oldY && newM === oldM) return
    ensureLoadedForModel(newKey)
  },
  { flush: 'post' }
)

onMounted(() => {
  ensureLoadedForModel(model.value)
})

</script>

<style scoped>
.calendar-wrap {
  min-width: 1100px;
}

.calendar-header {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-bottom: 10px;
  gap: 6px;
}

.month-title {
  font-weight: 600;
  padding: 0 6px;
}

/* Vnější wrapper eventu (Vuetify) – zruš background, click efekt, nastav full width + auto height + centrování */
:deep(.v-event) {
  background: transparent !important;
  width: 100% !important;
  margin: 0 auto 1px auto !important;
  height: auto !important;
  cursor: default !important;
}

/* zruš hover/overlay/ripple na celé položce */
:deep(.v-event .v-ripple__container),
:deep(.v-event__underlay),
:deep(.v-event__overlay) {
  display: none !important;
}

/* zruš “klikatelnost” wrapperu, ale nech odkazy/ikony uvnitř */
:deep(.v-event) {
  pointer-events: none !important;
}
.simple-item,
.simple-item a,
.simple-item .entry-link,
.simple-item button,
.simple-item [role="button"] {
  pointer-events: auto !important;
}

/* karta eventu v buňce dne */
.simple-item {
  padding: 6px 8px;
  margin: 2px auto;
  border-radius: 6px;
  font-size: 0.85rem;
  line-height: 1.2;
  display: block;
  width: 95%;
}


.event-name {
  font-weight: 700;
  text-decoration: none;
}

.race-name {
  font-style: italic;
  margin-top: 2px;
}

/* EVENT: chips vlevo, ikony vpravo */
.meta-event {
  margin-top: 6px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.chips-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: nowrap;
}

.icons-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: nowrap;
}

/* RACE: 3 chipy rovnoměrně (space-between) */
.meta-race {
  margin-top: 6px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.icons-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.entry-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

:deep(.v-calendar-weekly__day) {
  min-height: 150px !important;
}


/* dny mimo aktuální měsíc */
:deep(.v-calendar-weekly__day.v-outside) {
  background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
}

/* když má Vuetify uvnitř ještě wrapper, pokryj i ten */
:deep(.v-calendar-weekly__day.v-outside .v-calendar-weekly__day-content),
:deep(.v-calendar-weekly__day.v-outside .v-calendar-weekly__day-inner),
:deep(.v-calendar-weekly__day.v-outside .v-sheet) {
  background-color: transparent !important;
}



</style>
